export type Json = unknown;

export interface ApiError {
  code: string;
  message: string;
  fields?: Record<string, string>;
  details?: unknown;
  requestId?: string;
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  const fields = (error as Error & { fields?: Record<string, string> }).fields;
  return fields ? Object.values(fields).join(" ") : error.message;
}

let csrfCookie = "csrf-bootstrap";

export function setBootstrapCsrf(token: string | null): void {
  if (token) sessionStorage.setItem(csrfCookie, token);
  else sessionStorage.removeItem(csrfCookie);
}

export function getCsrf(): string | null {
  return sessionStorage.getItem(csrfCookie);
}

async function request<T>(
  method: string,
  path: string,
  body?: Json,
  options: { csrf?: boolean; headers?: Record<string, string>; signal?: AbortSignal; unwrap?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers ?? {}),
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (options.csrf !== false) {
    const t = getCsrf();
    if (t) headers["x-csrf-token"] = t;
  }
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: options.signal,
  });

  const text = await res.text();
  const payload = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = (payload as { error?: ApiError } | null)?.error;
    const e = new Error(err?.message ?? `HTTP ${res.status}`) as Error & {
      code?: string;
      status?: number;
      fields?: Record<string, string>;
      details?: unknown;
    };
    e.code = err?.code;
    e.status = res.status;
    e.fields = err?.fields;
    e.details = err?.details;
    throw e;
  }
  if (options.unwrap !== false && (payload as { data?: T } | null)?.data !== undefined) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, { signal }),
  getEnvelope: <T,>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, { signal, unwrap: false }),
  post: <T,>(path: string, body?: Json, csrf = true, headers?: Record<string, string>) => request<T>("POST", path, body, { csrf, headers }),
  put: <T,>(path: string, body?: Json) => request<T>("PUT", path, body),
  patch: <T,>(path: string, body?: Json) => request<T>("PATCH", path, body),
  delete: <T,>(path: string) => request<T>("DELETE", path),
  // raw URL for external use (e.g. /api/public/v1)
  postRaw: <T,>(path: string, body?: Json, headers?: Record<string, string>) => request<T>("POST", path, body, { csrf: false, headers }),
  getRaw: <T,>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, { csrf: false, signal }),
};
